<template>
    <div>
        <v-row>
            <v-col cols="4" v-for="({ text, value, formatter }) in totalCards" :key="text">
                <v-card height="127">
                    <v-card-subtitle class="">{{ text }}</v-card-subtitle>
                    <v-divider />
                    <v-card-text>
                        <div v-if="loading" style="height: 100%" class="d-flex justify-center align-center">
                            <v-icon size="30px" class="fa-spin" >
                                fa-spinner
                            </v-icon>                    
                        </div>
                        <div v-else>
                            <div class="display-1 text--primary">{{ formatter(value(totals)) }}</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-if="team">
            <v-col>
                <v-card>
                    <v-card-text>
                        <loader v-if="loading" />
                        <vue-apex-charts v-else height="400px" type="line" :options="chartOptions" :title="getChartTitle" :series="series" />
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <loader v-if="loading" />
                <v-card v-else>
                    <v-card-text>
                        <v-subheader>
                            <v-row>
                                <v-col v-for="header in headers" :key="header.text" @click="setSort(header.field)">
                                    {{ header.text }}
                                    <v-icon v-if="sortField === header.field && sortOrder">fa-angle-down</v-icon>
                                    <v-icon v-else-if="sortField === header.field">fa-angle-up</v-icon>
                                </v-col>
                            </v-row>
                        </v-subheader>
                        <v-divider />
                        <v-virtual-scroll height="70vh" item-height="64" :items="sortedAgents" bench="1000" class="striped-scroll">
                            <template v-slot:default="{ item: agent }">
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-row align="baseline">
                                            <v-col v-for="header in headers" :key="header.text">
                                                <span>{{ header.formatter(agent[header.field]) }}</span>
                                            </v-col>
                                        </v-row>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-virtual-scroll>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

export default {
	components: {
		VueApexCharts
	},
    data () {
        return {
            loading: true,
            agents: null,
            labels: [],
            series: [],
            sortField: 'total',
            sortOrder: true
        }
    },
    computed: {
        chartOptions () {
            return {
                title: {
                    text: this.department === 'tryg' ? 'Annulleringsprocent nysalg' : 'Annulleringsprocent total'
                },
                chart: {
                    toolbar: {
                        show: false
                    }
                },
                animations: {
                    enabled: false
                },
                stroke: {
                    show: true,
                    width: [0, 2, 2]
                },
                yaxis: {
                    labels: {
                        formatter: this.$options.filters.percentageDecimal
                    }
                },
                xaxis: {
                    categories: this.labels,
                    labels: {
                        hideOverlappingLabels: false,
                        rotate: -45,
                        rotateAlways: true
                    }
                },
                dataLabels: {
                    enabled: this.agents.length <= 30,
                    enabledOnSeries: [0],
                    formatter: this.$options.filters.percentageDecimal,
                    offsetY: -20,
                    style: {
                        colors: ['#000']
                    }
                },
                plotOptions: {
                    bar: {
                        dataLabels: {
                            position: 'top'
                        }
                    }
                },
                colors: ['#FF0000', '#00BB00', '#0000FF']
            }
        },
        getChartTitle () {
            if (this.department === 'tryg') return { text: 'Annulleringsprocent nysalg' }
            else if (this.department === 'alka') return { text: 'Annulleringsprocent nysalg' }
            else return { text: '' }
        },
        totalCards () {
            if (this.$store.getters['quality/department'] === 'tryg') {
                return [
                    {
                        text: 'Nettosalg',
                        value: (stats) => stats.realized,
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Annullering nysalg',
                        value: (stats) => stats.canceled,
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Annullering mersalg (nedsættelser)',
                        value: (stats) => stats.adjusted,
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Annullering total',
                        value: (stats) => stats.total,
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Annulleringsprocent nysalg',
                        value: (stats) => (stats.canceled / ((stats.realized + stats.canceled) || 1)),
                        formatter: this.$options.filters.percentageDecimal
                    },
                    {
                        text: 'Annulleringsprocent mersalg (nedsættelser)',
                        value: (stats) => (stats.adjusted / ((stats.realized + stats.adjusted) || 1)),
                        formatter: this.$options.filters.percentageDecimal
                    },
                    {
                        text: 'Annulleringsprocent total',
                        value: (stats) => (stats.total / ((stats.realized + stats.total) || 1)),
                        formatter: this.$options.filters.percentageDecimal
                    }
                ]
            } else if (this.$store.getters['quality/department'] === 'alka') {
                return [
                    {
                        text: 'Nysalg brutto',
                        value: (stats) => stats.grossNew,
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ændringer brutto',
                        value: (stats) => stats.grossAdjusted,
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Annullering nysalg',
                        value: (stats) => stats.canceled,
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Annullering mersalg (nedsættelser)',
                        value: (stats) => stats.adjusted,
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Annullering total',
                        value: (stats) => stats.total,
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Annulleringsprocent nysalg',
                        value: (stats) => (stats.canceled / (stats.grossNew || 1)),
                        formatter: this.$options.filters.percentageDecimal
                    },
                    {
                        text: 'Annulleringsprocent mersalg (nedsættelser)',
                        value: (stats) => (stats.adjusted / (stats.grossAdjusted || 1)),
                        formatter: this.$options.filters.percentageDecimal
                    },
                    {
                        text: 'Annulleringsprocent total',
                        value: (stats) => (stats.total / ((stats.grossNew + stats.grossAdjusted) || 1)),
                        formatter: this.$options.filters.percentageDecimal
                    }
                ]
            } else return []
        },
        headers () {
            if (this.$store.getters['quality/department'] === 'tryg') {
                return [
                    {
                        text: 'Navn',
                        field: 'name',
                        formatter: v => v
                    },
                    {
                        text: 'Nettosalg',
                        field: 'realized',
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ann. nysalg',
                        field: 'canceled',
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ann. mersalg (nedsættelser)',
                        field: 'adjusted',
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ann. total',
                        field: 'total',
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ann.% nysalg',
                        field: 'canceledPercentage',
                        formatter: this.$options.filters.percentageDecimal
                    },
                    {
                        text: 'Ann.% mersalg (nedsættelser)',
                        field: 'adjustedPercentage',
                        formatter: this.$options.filters.percentageDecimal
                    },
                    {
                        text: 'Ann.% total',
                        field: 'totalPercentage',
                        formatter: this.$options.filters.percentageDecimal
                    }
                ]
            } else if (this.$store.getters['quality/department'] === 'alka') {
                return [
                    {
                        text: 'Navn',
                        field: 'name',
                        formatter: v => v
                    },
                    {
                        text: 'Nysalg brutto',
                        field: 'grossNew',
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ændringer brutto',
                        field: 'grossAdjusted',
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ann. nysalg',
                        field: 'canceled',
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ann. mersalg (nedsættelser)',
                        field: 'adjusted',
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ann. total',
                        field: 'total',
                        formatter: this.$options.filters.separator
                    },
                    {
                        text: 'Ann.% nysalg',
                        field: 'canceledPercentage',
                        formatter: this.$options.filters.percentageDecimal
                    },
                    {
                        text: 'Ann.% mersalg (nedsættelser)',
                        field: 'adjustedPercentage',
                        formatter: this.$options.filters.percentageDecimal
                    },
                    {
                        text: 'Ann.% total',
                        field: 'totalPercentage',
                        formatter: this.$options.filters.percentageDecimal
                    }
                ]   
            } else return [] 
        },
        filteredAgents () {
            if (!this.agents) return []
            return this.agents.filter(agent => {
                return agent.name.toLowerCase().includes(this.$store.state.quality.searchString.toLowerCase())
            })
        },
        sortedAgents () {
            const order = this.sortOrder ? -1 : 1
            return [...this.filteredAgents].sort((a, b) => a[this.sortField] > b[this.sortField] ? order : -order)
        },
        department () {
            return this.$store.getters['quality/department']
        },
        year () {
            return this.$store.state.quality.year
        },
        startWeek () {
            return this.$store.state.quality.startWeek
        },
        endWeek () {
            return this.$store.state.quality.endWeek
        },
        ytd () {
            return this.$store.state.quality.ytd
        },
        inactive () {
            return this.$store.state.quality.inactive
        },
        team () {
            return this.$store.state.quality.team
        },
        totals () {
            return this.$reduceNested(this.agents, ['realized', 'grossNew', 'grossAdjusted', 'canceled', 'adjusted', 'total'])
        },
        searchString () {
            return this.$store.state.quality.searchString
        }
    },
    methods: {
		setSort(field) {
			if (this.sortField === field) this.sortOrder = !this.sortOrder;
			else {
                this.sortField = field
                this.sortOrder = true
            }
        },
        async getData () {
            this.loading = true
            const response = await this.$http().get('quality/status/cancelations', {
                params: {
                    year: this.$store.state.quality.year,
                    startWeek: this.$store.state.quality.startWeek,
                    endWeek: this.$store.state.quality.endWeek,
                    team: this.team,
                    department: this.department,
                    inactive: this.$store.state.quality.inactive
                }
            })
            this.agents = response.data.agents
            this.labels = response.data.labels
            this.series = response.data.series
            this.loading = false
        }
    },
    watch: {
        selectedAgent: function () {
            this.getComplaints(this.selectedAgent)
        },
        department: function () {
            this.getData()
        },
        year: function () {
            this.getData()
        },
        startWeek: function () {
            this.getData()
        },
        endWeek: function () {
            this.getData()
        },
        ytd: function () {
            this.getData()
        },
        inactive () {
            this.getData()
        },
        team: function () {
            this.getData()
        }
    },
    created () {
        this.$store.state.quality.searchString = ''
        this.getData()
    }
}
</script>